import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { BookOpen, Lock, ArrowLeft } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: "",
    password: ""
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      localStorage.setItem("mindary_token", response.data.access_token);
      toast.success("Velkommen!", {
        description: "Du er nå logget inn."
      });
      navigate("/admin/dashboard");
    } catch (error) {
      toast.error("Innlogging feilet", {
        description: error.response?.data?.detail || "Vennligst sjekk brukernavn og passord."
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="mb-8 text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8" data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4" />
            Tilbake til forsiden
          </Link>
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center">
              <BookOpen className="w-8 h-8 text-primary-foreground" />
            </div>
          </div>
          <h1 className="font-display text-2xl font-semibold text-foreground tracking-tight">
            mindary
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Admin Panel</p>
        </div>

        <Card className="bg-card border-border/50 shadow-lg">
          <CardHeader className="text-center pb-4">
            <CardTitle className="font-display text-xl flex items-center justify-center gap-2">
              <Lock className="w-5 h-5 text-primary" />
              Logg inn
            </CardTitle>
            <CardDescription>
              Skriv inn dine admin-opplysninger
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="admin-login-form">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-foreground">Brukernavn</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="admin"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  className="bg-background/50 border-border focus:ring-primary"
                  data-testid="admin-username-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-foreground">Passord</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="bg-background/50 border-border focus:ring-primary"
                  data-testid="admin-password-input"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full rounded-full hover:scale-105 transition-transform"
                disabled={isLoading}
                data-testid="admin-login-button"
              >
                {isLoading ? "Logger inn..." : "Logg inn"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-6">
          Glemt passord? <Link to="/admin/reset-password" className="text-primary hover:underline">Tilbakestill her</Link>
        </p>
      </motion.div>
    </div>
  );
}
